import { useEffect } from 'react';
import { useBotContext } from '../components/useBotContext';
import { IBotData } from '../models';
import { bot_tunnel_configuration_ids } from '../helpers/data/bot';

export const useOpeningAccessory = (
  tunnelIndex: number,
  sectionIndex: number,
) => {
  const { value: botData, setValue } = useBotContext();

  // Auto-manage opening accessory for first & last section
  useEffect(() => {
    if (!botData?.tunnels) return;

    setValue((draft: IBotData) => {
      if (!draft.tunnels) return;
      const tunnel = draft.tunnels[tunnelIndex];
      if (!tunnel || !tunnel.sections || tunnel.sections.length === 0) return;

      const lastSectionIndex = tunnel.sections.length - 1;
      const currentSection = tunnel.sections[sectionIndex];
      if (!currentSection) return;

      currentSection.accessories ||= [];
      const isCurrentlyLast = sectionIndex === lastSectionIndex;
      const isCurrentlyFirst = sectionIndex === 0;
      
      const createInFirstSection =
        tunnel.airflow_direction_id === 1 ||
        botData.tunnels_have_same_directions;
      const createInLastSection =
        tunnel.airflow_direction_id === 2 ||
        botData.tunnels_have_same_directions;

      // Handle opening for first section (mounting_plane_id: 4)
      if (isCurrentlyFirst && createInFirstSection) {
        const firstSectionOpeningIndex = currentSection.accessories.findIndex(
          (acc) => acc.type === 'opening' && acc.mounting_plane_id === 4,
        );

        // Auto-create opening if the first section doesn't have one
        if (firstSectionOpeningIndex === -1) {
          currentSection.accessories.push({
            type: 'opening',
            name: 'Opening',
            mounting_plane_id: 4,
            outside_airflow:
              tunnel.add_outside_airflow === true ? true : undefined,
            autoAdded: true, // Mark as automatically added
          });
        } else {
          // Ensure outside_airflow is set based on tunnel.add_outside_airflow
          const existingOpening =
            currentSection.accessories[firstSectionOpeningIndex];
          if (existingOpening) {
            if (tunnel.add_outside_airflow === true) {
              existingOpening.outside_airflow = true;
            } else {
              // Only remove outside_airflow if it was auto-added, otherwise preserve user settings
              if (existingOpening.autoAdded) {
                existingOpening.outside_airflow = undefined;
              }
            }
          }
        }
      } else {
        // For non-first sections: remove auto-added openings with mounting_plane_id 4
        const autoAddedFirstOpeningIndex = currentSection.accessories.findIndex(
          (acc) =>
            acc.type === 'opening' &&
            acc.mounting_plane_id === 4 &&
            acc.autoAdded === true,
        );
        if (autoAddedFirstOpeningIndex !== -1) {
          currentSection.accessories.splice(autoAddedFirstOpeningIndex, 1);
        }
      }

      // Handle opening for last section (mounting_plane_id: 3)
      if (isCurrentlyLast && createInLastSection) {
        // For the last section: ensure it has an opening
        const openingIndex = currentSection.accessories.findIndex(
          (acc) => acc.type === 'opening' && acc.mounting_plane_id === 3,
        );

        // Auto-create opening if the last section doesn't have one
        if (openingIndex === -1) {
          currentSection.accessories.push({
            type: 'opening',
            name: 'Opening',
            mounting_plane_id: 3,
            autoAdded: true, // Mark as automatically added
          });
        }
      } else {
        // For non-last sections: remove auto-added openings with mounting_plane_id 3 only
        const autoAddedOpeningIndex = currentSection.accessories.findIndex(
          (acc) =>
            acc.type === 'opening' &&
            acc.mounting_plane_id === 3 &&
            acc.autoAdded === true,
        );
        if (autoAddedOpeningIndex !== -1) {
          currentSection.accessories.splice(autoAddedOpeningIndex, 1);
        }
      }
    });
  }, [
    botData?.tunnels_have_same_directions,
    sectionIndex,
    tunnelIndex,
    botData.tunnels,
    botData?.tunnels?.[tunnelIndex]?.add_outside_airflow,
  ]);

  // Auto-manage return airflow opening for first section of first tunnel
  useEffect(() => {
    if (!botData?.tunnels) return;
    if (tunnelIndex !== 0 || sectionIndex !== 0) return; // Only for first section of first tunnel
    setValue((draft: IBotData) => {
      if (!draft.tunnels || draft.tunnels.length === 0) return;
      const returnTunnelIndex = draft.tunnels.findIndex(
        (t) => t.add_return_airflow,
      );
      const filteredTunnels = draft.tunnels.filter(
        (t) => !t.add_return_airflow,
      );
      filteredTunnels.forEach((element) => {
        const firstSection = element.sections[0];
        if (!firstSection) return;
        const returnAirflowOpeningIndex = firstSection?.accessories.findIndex(
          (acc) =>
            acc.type === 'opening' &&
            (acc.mounting_plane_id === 1 || acc.mounting_plane_id === 2) &&
            acc.autoAdded === true,
        );
        if (returnAirflowOpeningIndex !== -1)
          firstSection.accessories.splice(returnAirflowOpeningIndex, 1);
      });
      const returnTunnel = draft.tunnels[returnTunnelIndex];
      if (
        !returnTunnel ||
        !returnTunnel.sections ||
        returnTunnel.sections.length === 0
      )
        return;

      const firstSection = returnTunnel.sections[0];
      if (!firstSection) return;
      const tunnelConfigId = botData.tunnel_configuration as 1 | 2 | 3 | 4 | 5;
      const mounting_plane_id =
        returnTunnelIndex === 0 ||
        bot_tunnel_configuration_ids[tunnelConfigId] === 9
          ? 1
          : 2;
      firstSection.accessories ||= [];
      const returnAirflowOpeningIndex = firstSection.accessories.findIndex(
        (acc) =>
          acc.type === 'opening' &&
          (acc.mounting_plane_id === 1 || acc.mounting_plane_id === 2) &&
          acc.autoAdded === true,
      );

      // If tunnel has add_return_airflow true, add opening
      if (returnTunnel.add_return_airflow === true) {
        if (returnAirflowOpeningIndex === -1) {
          firstSection.accessories.push({
            type: 'opening',
            name: 'Opening',
            mounting_plane_id,
            return_airflow: true,
            autoAdded: true, // Mark as automatically added
          });
        } else {
          // Ensure return_airflow is set to true if opening already exists
          const existingOpening =
            firstSection.accessories[returnAirflowOpeningIndex];
          if (existingOpening) {
            existingOpening.return_airflow = true;
            existingOpening.mounting_plane_id = mounting_plane_id;
          }
        }
      } else {
        // Remove auto-added return airflow opening if add_return_airflow is false
        if (returnAirflowOpeningIndex !== -1) {
          firstSection.accessories.splice(returnAirflowOpeningIndex, 1);
        }
      }
    });
  }, [
    sectionIndex,
    botData?.tunnels?.[tunnelIndex]?.add_return_airflow,
    setValue,
  ]);
};
